<template>
  <main class="physical-page">
    <AppNavBar
      backgroundColor="#fff"
      :title="t('wallet.activatePhysicalCard')"
    />

    <section class="form-section">
      <label class="field-label">{{ t("wallet.cardNumber") }}</label>
      <van-field
        v-model="cardNum"
        class="card-input"
        clearable
        :placeholder="t('wallet.enterCardNumber')"
      />
    </section>

    <van-button
      block
      round
      type="primary"
      class="submit-btn"
      :loading="submitting"
      :disabled="!canSubmit"
      @click="openPasswordDialog"
    >
      {{ t("common.submit") }}
    </van-button>

    <PayPasswordPopup
      v-model:show="showPasswordDialog"
      v-model:password="passwordInput"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :forgot-text="t('auth.forgotPasswordRecovery')"
      show-forgot
      :confirm-disabled="!passwordInput.trim()"
      @cancel="closePasswordDialog(false)"
      @closed="handlePasswordPopupClosed"
      @forgot="handleForgotPassword"
      @confirm="submitPasswordDialog"
    />
  </main>
</template>

<script setup>
import { computed, ref } from "vue";
import { closeToast, showFailToast, showLoadingToast } from "vant";
import { showPassedToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import PayPasswordPopup from "@/components/PayPasswordPopup.vue";
import { payPwdCheck } from "@/api/auth";
import { activePhysicalCard } from "@/api/card";
import { useProfileStore } from "@/stores/profile";
import { navigateWithState } from "@/utils/route-state";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();

const cardNum = ref("");
const showPasswordDialog = ref(false);
const passwordInput = ref("");
const submitting = ref(false);
let passwordResolver = null;

const canSubmit = computed(() => String(cardNum.value || "").trim().length > 0);

function verifyWalletPassword() {
  passwordInput.value = "";
  showPasswordDialog.value = true;
  return new Promise((resolve) => {
    passwordResolver = resolve;
  });
}

function closePasswordDialog(result = false) {
  if (passwordResolver) {
    passwordResolver(result);
    passwordResolver = null;
  }
  showPasswordDialog.value = false;
}

function handlePasswordPopupClosed() {
  passwordInput.value = "";
}

function handleForgotPassword() {
  closePasswordDialog(false);
  router.push("/auth/forgot-pay-password");
}

function goActivatedCardIndex() {
  navigateWithState(router, "/card/index", {
    namespace: "home-card",
    replace: true,
    payload: {
      tab: "physical",
    },
  });
}

async function submitPasswordDialog() {
  const payPwd = String(passwordInput.value || "").trim();
  if (!payPwd) {
    showFailToast(t("auth.enterPassword"));
    return;
  }

  const result = await payPwdCheck({ payPwd });
  if (!result) {
    showFailToast(t("auth.passwordError"));
    return;
  }

  closePasswordDialog(true);
}

async function openPasswordDialog() {
  if (!canSubmit.value || submitting.value) {
    return;
  }

  const verified = await verifyWalletPassword();
  if (!verified) {
    return;
  }
  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }

  submitting.value = true;
  showLoadingToast({
    message: t("common.submitting"),
    forbidClick: true,
    duration: 0,
  });
  try {
    await activePhysicalCard({
      address: profileStore.platformAddress || null,
      cardNum: String(cardNum.value || "").trim(),
    });
    closeToast();
    showPassedToast(t("wallet.activateSuccess"));
    setTimeout(() => {
      goActivatedCardIndex();
    }, 500);
  } catch (error) {
    closeToast();
    showFailToast(error?.message || error?.msg || t("common.retryLater"));
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped lang="scss">
.physical-page {
  min-height: var(--app-viewport-height);
  padding: 14px 16px 32px;
  background: #ffffff;
}

.form-section {
  padding-top: 8px;
}

.field-label {
  display: block;
  margin-bottom: 10px;
  color: #000;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
}

.card-input {
  padding: 0;
  border-radius: 12px;

  :deep(.van-field__body) {
    min-height: 52px;
    padding: 0 14px;
    background: #f5f5f5;
  }

  :deep(.van-field__control) {
    color: #666;
    font-size: 15px;
    line-height: 20px;
  }

  :deep(.van-field__control::placeholder) {
    color: #b7b7b7;
  }
}

.submit-btn {
  margin-top: 34px;
  height: 52px;

  :deep(.van-button__text) {
    font-size: 16px;
    font-weight: 500;
  }
}
</style>
